/**
 * RelatedPersonsDrawer — gestão das pessoas relacionadas de um Bond (vínculos
 * factuais, ADR-129), surface na aba Cliente da conversa (bondId em mãos).
 *
 * Lista (toggle incluir encerrados) + adicionar (personId honest-defer + tipo +
 * início + fonte) + por vínculo ativo editar/encerrar (RelatedPersonCard).
 * Reutiliza o Drawer do DS; conteúdo escopado sob `.brp-scope` (CSS co-located).
 *
 * É o 2º dos 3 sub-recursos ortogonais do Bond (participants / related-persons /
 * authorized-contacts) — NÃO implica acesso ou autoridade, só estrutura factual.
 * Gate `bond-basic` aplicado no chamador (TabCliente) + server-side.
 */

import { Drawer } from '@lume/ui-components';
import { useState } from 'react';

import {
  useAddRelatedPerson,
  useManagedRelatedPersons,
} from '../../../hooks/use-bond-related-persons';

import './related-persons.css';
import {
  buildAddRequest,
  initialAddForm,
  validateAddForm,
  type RelatedPersonFormState,
} from './related-persons-form';
import { ADD_SOURCES, sourceLabel } from './related-persons-labels';
import { RelatedPersonCard } from './RelatedPersonCard';
import { RelationshipTypePicker } from './RelationshipTypePicker';

/**
 * Conteúdo (sem o Drawer) — reutilizável pelo RelatedPersonsDrawer (aba Cliente)
 * e pelo BondManagementDrawer com abas (ficha da Pessoa). `bondId` já resolvido.
 */
export function RelatedPersonsPanel({ bondId }: { bondId: string }): JSX.Element {
  const [includeTerminated, setIncludeTerminated] = useState(false);
  const [adding, setAdding] = useState(false);

  const { data: persons, isLoading, isError } = useManagedRelatedPersons(bondId, includeTerminated);

  return (
    <div className="brp-scope">
      <p className="brp-intro">
        Quem está factualmente relacionado a este vínculo — família, sócios, contexto (ADR-129).
        Registra a estrutura factual e NÃO implica acesso nem autoridade (isso é tratado em contatos
        autorizados e participantes).
      </p>

      <div className="brp-toolbar">
        <label className="brp-toggle">
          <input
            type="checkbox"
            checked={includeTerminated}
            onChange={(e) => setIncludeTerminated(e.target.checked)}
          />
          <span>Incluir encerrados</span>
        </label>
        {!adding && (
          <button
            type="button"
            className="brp-btn brp-btn--primary"
            onClick={() => setAdding(true)}
          >
            + Adicionar pessoa
          </button>
        )}
      </div>

      {adding && <AddRelatedPersonForm bondId={bondId} onDone={() => setAdding(false)} />}

      {isLoading ? (
        <p className="brp-muted">Carregando pessoas relacionadas…</p>
      ) : isError ? (
        <p className="brp-alert brp-alert--error" role="alert">
          Não foi possível carregar as pessoas relacionadas.
        </p>
      ) : persons.length === 0 ? (
        <p className="brp-empty">
          Nenhuma pessoa relacionada{includeTerminated ? '' : ' ativa'} neste vínculo.
        </p>
      ) : (
        <div className="brp-list">
          {persons.map((p) => (
            <RelatedPersonCard key={p.id} bondId={p.bondId} person={p} />
          ))}
        </div>
      )}
    </div>
  );
}

export function RelatedPersonsDrawer({
  bondId,
  onClose,
}: {
  bondId: string | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Drawer
      isOpen={bondId !== null}
      onClose={onClose}
      title="Pessoas relacionadas ao vínculo"
      width="lg"
    >
      {bondId !== null && <RelatedPersonsPanel bondId={bondId} />}
    </Drawer>
  );
}

function AddRelatedPersonForm({
  bondId,
  onDone,
}: {
  bondId: string;
  onDone: () => void;
}): JSX.Element {
  const add = useAddRelatedPerson(bondId);
  const [form, setForm] = useState<RelatedPersonFormState>(initialAddForm);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(): void {
    const v = validateAddForm(form);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    add.mutate(buildAddRequest(form), {
      onSuccess: onDone,
      onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao adicionar.'),
    });
  }

  return (
    <div className="brp-inline brp-inline--add">
      <label className="brp-field" htmlFor="add-person-id">
        <span className="brp-field__label">
          ID da pessoa relacionada
          <span className="brp-defer-tag">person-picker diferido</span>
        </span>
        <input
          id="add-person-id"
          className="brp-input"
          placeholder="person_…"
          value={form.personId}
          onChange={(e) => setForm((p) => ({ ...p, personId: e.target.value }))}
        />
      </label>

      <label className="brp-field" htmlFor="add-type">
        <span className="brp-field__label">Tipo de relacionamento</span>
        <RelationshipTypePicker
          id="add-type"
          value={form.relationshipType}
          onChange={(t) => setForm((p) => ({ ...p, relationshipType: t }))}
        />
      </label>

      <label className="brp-field" htmlFor="add-started-at">
        <span className="brp-field__label">Início (opcional)</span>
        <input
          id="add-started-at"
          type="date"
          className="brp-input"
          value={form.startedAt}
          onChange={(e) => setForm((p) => ({ ...p, startedAt: e.target.value }))}
        />
      </label>

      <label className="brp-field" htmlFor="add-source">
        <span className="brp-field__label">Fonte</span>
        <select
          id="add-source"
          className="brp-input brp-select"
          value={form.source}
          onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
        >
          {ADD_SOURCES.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </select>
      </label>

      {error !== null && (
        <p className="brp-alert brp-alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="brp-inline__actions">
        <button type="button" className="brp-btn brp-btn--ghost" onClick={onDone}>
          Cancelar
        </button>
        <button
          type="button"
          className="brp-btn brp-btn--primary"
          disabled={add.isPending}
          onClick={handleSubmit}
        >
          {add.isPending ? 'Adicionando…' : 'Adicionar pessoa'}
        </button>
      </div>
    </div>
  );
}
